import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {AVR_ENABLE_GTM} from '../config/env';
import {
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_STATIC_URL_MAPPING,
} from '../constants/analyticsConstants';
import {
  GAAuto,
  GAContextSelectedInfo,
  GAErrorInfo,
  GAPageInfo,
  TrackGAEventParams,
} from '../types/analyticsTypes';
import {setPrevPage} from '../redux/slice/analyticsSlice';
import {RootState} from '../redux/store';
import {generatePageAndSection, pushToDataLayer} from '../utils/analyticsUtils';

const isAnalyticsEnabled =
  AVR_ENABLE_GTM === 'true' || AVR_ENABLE_GTM === true;

export function useAnalytics() {
  const userFromStore = useSelector((state: RootState) => state.analytics.user);
  const prevPage = useSelector((state: RootState) => state.analytics.prevPage);
  const contextSelectedFromStore = useSelector(
    (state: RootState) => state.analytics.contextSelected
  );
  const location = useLocation();
  const dispatch = useDispatch();

  const buildPagePayload = (page: GAPageInfo): GAPageInfo => ({
    ...page,
    ...(prevPage?.prev_page_name && {prev_page_name: prevPage.prev_page_name}),
    ...(prevPage?.prev_page_url && {prev_page_url: prevPage.prev_page_url}),
  });

  const resolvePage = (page?: GAPageInfo | GAAuto): GAPageInfo => {
    if (page && page !== GA_DATA_MODE_AUTO) {
      return buildPagePayload(page);
    }

    const mapping = generatePageAndSection(location.pathname + location.search);

    return buildPagePayload({
      name: mapping.page,
      section: mapping.section,
    });
  };

  const resolveUser = (user?: typeof userFromStore | GAAuto) => {
    if (user && user !== GA_DATA_MODE_AUTO) return user;
    return userFromStore;
  };

  const resolveContextSelected = (
    eventName: TrackGAEventParams['eventName'],
    contextSelected?: GAContextSelectedInfo
  ) => {
    if (contextSelected) {
      return contextSelected;
    }

    if (eventName === GA_EVENT_NAMES.PAGE_VIEW && contextSelectedFromStore) {
      return contextSelectedFromStore;
    }

    return undefined;
  };

  function trackGAEvent({
    eventName = GA_EVENT_NAMES.PAGE_VIEW,
    page,
    subPage,
    user,
    click,
    error,
    contextSelected,
    fileExtension,
  }: TrackGAEventParams = {}) {
    if (!isAnalyticsEnabled) return;

    const resolvedContextSelected = resolveContextSelected(
      eventName,
      contextSelected
    );

    pushToDataLayer({
      eventName,
      ...(page && {page: resolvePage(page)}),
      ...(user && {user: resolveUser(user)}),
      ...(subPage && {subPage}),
      ...(click && {click}),
      ...(error && {
        error: {
          text: error.text,
          ...(error.code && {code: error.code}),
          ...(error.type && {type: error.type}),
        },
      }),
      ...(resolvedContextSelected && {contextSelected: resolvedContextSelected}),
      ...(fileExtension && {fileExtension}),
    });

    if (eventName === GA_EVENT_NAMES.PAGE_VIEW) {
      const pathname = location.pathname + location.search;
      if (prevPage.prev_page_url !== pathname) {
        dispatch(setPrevPage(GA_STATIC_URL_MAPPING[pathname] || pathname));
      };
    }
  }

  function trackUserError(error: GAErrorInfo) {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.ERROR,
      page: GA_DATA_MODE_AUTO,
      user: GA_DATA_MODE_AUTO,
      error,
    });
  }

  return {
    trackGAEvent,
    trackUserError,
  };
}
